import { useState, useRef } from "react";
import usePageLoader from '../../hooks/usePageLoader';
import { ShimmerBox } from '../../components/common/Shimmer';
import {
  FiSend, FiX, FiPlus, FiSave, FiMessageCircle,
} from "react-icons/fi";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";

function AppearanceSkeleton() {
  return (
    <div className="flex-1 overflow-hidden flex">
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-white border rounded-xl p-5 space-y-4">
            <ShimmerBox h="h-4" w="w-40" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[...Array(4)].map((_, j) => (
                <div key={j} className="space-y-1.5">
                  <ShimmerBox h="h-3" w="w-24" />
                  <ShimmerBox h="h-10" w="w-full" rounded="rounded-lg" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="w-80 shrink-0 border-l bg-gray-50 p-5 space-y-4">
        <ShimmerBox h="h-4" w="w-28" />
        <ShimmerBox h="h-96" w="w-full" rounded="rounded-2xl" />
      </div>
    </div>
  );
}

export default function Appearance() {
  const { chatbotId } = useParams();
  const { selected } = useSelector((s) => s.chatbots);
  const loading = usePageLoader();

  /* ── Chatbot Identity ── */
  const [botName, setBotName] = useState(selected?.chatbot_name || "Support Bot");
  const [tagline, setTagline] = useState("Typically replies instantly");
  const [welcome, setWelcome] = useState("Hi there! 👋 How can I help you today?");
  const [placeholder, setPlaceholder] = useState("Ask me anything...");

  /* ── Colors ── */
  const [primaryColor, setPrimaryColor] = useState("#4F46E5");
  const [botBubbleBg, setBotBubbleBg] = useState("#f3f4f6");
  const [botBubbleText, setBotBubbleText] = useState("#111827");
  const [userBubbleBg, setUserBubbleBg] = useState("#4F46E5");
  const [userBubbleText, setUserBubbleText] = useState("#ffffff");
  const [chatBg, setChatBg] = useState("#ffffff");

  /* ── Typography ── */
  const [fontSize, setFontSize] = useState(14);
  const [fontFamily, setFontFamily] = useState("Inter");

  /* ── Chat Window ── */
  const [chatHeight, setChatHeight] = useState(70);
  const [borderRadius, setBorderRadius] = useState(16);
  const [showShadow, setShowShadow] = useState(true);

  /* ── Launcher Button ── */
  const [position, setPosition] = useState("bottom-right");
  const [iconSize, setIconSize] = useState("medium");
  const [iconShape, setIconShape] = useState("circle");
  const [bottomDist, setBottomDist] = useState(20);
  const [sideDist, setSideDist] = useState(20);
  const [showBubble, setShowBubble] = useState(true);
  const [bubbleText, setBubbleText] = useState("👋 Need help? Chat with us!");

  /* ── Avatars ── */
  const [botAvatar, setBotAvatar] = useState(null);
  const [userAvatar, setUserAvatar] = useState(null);

  /* ── Quick Replies ── */
  const [suggestions, setSuggestions] = useState([
    "What is NexaGPT?",
    "How do I get started?",
    "View pricing",
  ]);
  const [newSuggestion, setNewSuggestion] = useState("");

  /* ── Branding ── */
  const [showBranding, setShowBranding] = useState(true);
  const [brandText, setBrandText] = useState("");

  /* ── Advanced ── */
  const [customCSS, setCustomCSS] = useState("");

  /* ── Save ── */
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 900));
    setSaving(false);
    toast.success("Appearance saved successfully!");
  };

  const iconSizeMap = { small: 44, medium: 52, large: 60 };
  const shapeMap = { circle: "9999px", rounded: "14px", square: "8px" };

  return (
    <div className="-m-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>

      {/* Sticky header */}
      <div className="shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Appearance</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Customize how your chatbot looks and feels on your website
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 transition disabled:opacity-70"
        >
          {saving ? (
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <FiSave size={14} />
          )}
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {loading ? <AppearanceSkeleton /> : <div className="flex-1 overflow-y-auto p-6 flex gap-6 items-start">

        {/* ── LEFT: settings panels ── */}
        <div className="flex-1 min-w-0 flex flex-col gap-5">

          {/* 1. Chatbot Identity */}
          <SectionCard
            title="Chatbot Identity"
            subtitle="Set your chatbot's display name, greeting, and input placeholder."
          >
            <Field label="Chatbot Name">
              <TextInput value={botName} onChange={setBotName} />
            </Field>
            <Field label="Tagline" hint="Shown below the bot name in the chat header.">
              <TextInput value={tagline} onChange={setTagline} />
            </Field>
            <Field label="Welcome Message" hint="First message users see when they open the chat.">
              <TextareaInput value={welcome} onChange={setWelcome} />
            </Field>
            <Field label="Input Placeholder">
              <TextInput value={placeholder} onChange={setPlaceholder} />
            </Field>
          </SectionCard>

          {/* 2. Colors */}
          <SectionCard
            title="Colors & Theme"
            subtitle="Control the chatbot's color scheme to match your brand."
          >
            <div className="grid grid-cols-2 gap-4">
              <Field label="Primary / Header Color">
                <ColorPicker value={primaryColor} onChange={setPrimaryColor} />
              </Field>
              <Field label="Chat Background">
                <ColorPicker value={chatBg} onChange={setChatBg} />
              </Field>
              <Field label="Bot Bubble Background">
                <ColorPicker value={botBubbleBg} onChange={setBotBubbleBg} />
              </Field>
              <Field label="Bot Bubble Text">
                <ColorPicker value={botBubbleText} onChange={setBotBubbleText} />
              </Field>
              <Field label="User Bubble Background">
                <ColorPicker value={userBubbleBg} onChange={setUserBubbleBg} />
              </Field>
              <Field label="User Bubble Text">
                <ColorPicker value={userBubbleText} onChange={setUserBubbleText} />
              </Field>
            </div>
          </SectionCard>

          {/* 3. Typography */}
          <SectionCard
            title="Typography"
            subtitle="Font size and family used inside the chat window."
          >
            <div className="grid grid-cols-2 gap-5">
              <Field label={`Font Size — ${fontSize}px`}>
                <input
                  type="range" min={11} max={18} value={fontSize}
                  onChange={(e) => setFontSize(Number(e.target.value))}
                  className="w-full accent-primary mt-1"
                />
                <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                  <span>11px</span><span>18px</span>
                </div>
              </Field>
              <Field label="Font Family">
                <select
                  value={fontFamily}
                  onChange={(e) => setFontFamily(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {["Inter", "Poppins", "Roboto", "Open Sans", "Lato", "System Default"].map((f) => (
                    <option key={f}>{f}</option>
                  ))}
                </select>
              </Field>
            </div>
          </SectionCard>

          {/* 4. Chat Window */}
          <SectionCard
            title="Chat Window"
            subtitle="Adjust the size, shape, and visual style of the chat panel."
          >
            <div className="grid grid-cols-2 gap-5">
              <Field label={`Window Height — ${chatHeight}vh`}>
                <input
                  type="range" min={40} max={90} value={chatHeight}
                  onChange={(e) => setChatHeight(Number(e.target.value))}
                  className="w-full accent-primary mt-1"
                />
                <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                  <span>40vh</span><span>90vh</span>
                </div>
              </Field>
              <Field label={`Corner Radius — ${borderRadius}px`}>
                <input
                  type="range" min={0} max={24} value={borderRadius}
                  onChange={(e) => setBorderRadius(Number(e.target.value))}
                  className="w-full accent-primary mt-1"
                />
                <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                  <span>0px</span><span>24px</span>
                </div>
              </Field>
            </div>
            <Field label="Drop Shadow">
              <Toggle value={showShadow} onChange={setShowShadow} label={showShadow ? "Enabled" : "Disabled"} />
            </Field>
          </SectionCard>

          {/* 5. Launcher Button */}
          <SectionCard
            title="Launcher Button"
            subtitle="Configure the floating button that opens and closes your chatbot."
          >
            <Field label="Position on Page">
              <div className="grid grid-cols-2 gap-2 w-48">
                {[
                  { key: "top-left", label: "↖ Top Left" },
                  { key: "top-right", label: "↗ Top Right" },
                  { key: "bottom-left", label: "↙ Bottom Left" },
                  { key: "bottom-right", label: "↘ Bottom Right" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setPosition(key)}
                    className={`text-xs px-2 py-1.5 rounded-lg border transition ${
                      position === key
                        ? "bg-primary text-white border-primary"
                        : "border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Field>

            <div className="grid grid-cols-2 gap-5">
              <Field label="Button Size">
                <div className="flex gap-2">
                  {["small", "medium", "large"].map((s) => (
                    <button
                      key={s}
                      onClick={() => setIconSize(s)}
                      className={`flex-1 py-1.5 text-xs rounded-lg border capitalize transition ${
                        iconSize === s
                          ? "bg-primary text-white border-primary"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {s === "small" ? "S" : s === "medium" ? "M" : "L"}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Button Shape">
                <div className="flex gap-2">
                  {[
                    { key: "circle", label: "Circle" },
                    { key: "rounded", label: "Rounded" },
                    { key: "square", label: "Square" },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setIconShape(key)}
                      className={`flex-1 py-1.5 text-xs rounded-lg border transition ${
                        iconShape === key
                          ? "bg-primary text-white border-primary"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label={`Bottom Distance — ${bottomDist}px`}>
                <input
                  type="range" min={8} max={48} value={bottomDist}
                  onChange={(e) => setBottomDist(Number(e.target.value))}
                  className="w-full accent-primary mt-1"
                />
              </Field>
              <Field label={`Side Distance — ${sideDist}px`}>
                <input
                  type="range" min={8} max={48} value={sideDist}
                  onChange={(e) => setSideDist(Number(e.target.value))}
                  className="w-full accent-primary mt-1"
                />
              </Field>
            </div>

            <Field label="Pre-chat Bubble Message">
              <div className="flex items-center gap-3 mb-2">
                <Toggle
                  value={showBubble}
                  onChange={setShowBubble}
                  label={showBubble ? "Enabled" : "Disabled"}
                />
              </div>
              {showBubble && (
                <TextInput
                  value={bubbleText}
                  onChange={setBubbleText}
                  placeholder="👋 Need help? Chat with us!"
                />
              )}
            </Field>
          </SectionCard>

          {/* 6. Avatars */}
          <SectionCard
            title="Avatars"
            subtitle="Upload custom icons for the bot, user, and live agent."
          >
            <div className="grid grid-cols-3 gap-4">
              <AvatarUpload
                label="Bot Avatar"
                value={botAvatar}
                onChange={setBotAvatar}
                fallback="🤖"
              />
              <AvatarUpload
                label="User Avatar"
                value={userAvatar}
                onChange={setUserAvatar}
                fallback="👤"
              />
              <AvatarUpload
                label="Agent Avatar"
                value={null}
                onChange={() => {}}
                fallback="🎧"
                locked
              />
            </div>
          </SectionCard>

          {/* 7. Quick Replies */}
          <SectionCard
            title="Quick Replies"
            subtitle="Suggestion chips shown before the user sends their first message."
          >
            <div className="flex flex-wrap gap-2 mb-3">
              {suggestions.map((s, i) => (
                <span
                  key={i}
                  className="flex items-center gap-1 text-xs bg-primary/10 text-primary px-3 py-1.5 rounded-full"
                >
                  {s}
                  <button
                    onClick={() => setSuggestions(suggestions.filter((_, idx) => idx !== i))}
                    className="ml-0.5 text-primary/50 hover:text-red-500 transition"
                  >
                    <FiX size={11} />
                  </button>
                </span>
              ))}
              {suggestions.length === 0 && (
                <p className="text-sm text-gray-400">No quick replies yet. Add one below.</p>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newSuggestion}
                onChange={(e) => setNewSuggestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newSuggestion.trim()) {
                    setSuggestions([...suggestions, newSuggestion.trim()]);
                    setNewSuggestion("");
                  }
                }}
                placeholder="Add a suggestion and press Enter…"
                className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <button
                onClick={() => {
                  if (newSuggestion.trim()) {
                    setSuggestions([...suggestions, newSuggestion.trim()]);
                    setNewSuggestion("");
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 transition"
              >
                <FiPlus size={14} /> Add
              </button>
            </div>
          </SectionCard>

          {/* 8. Branding */}
          <SectionCard
            title="Branding"
            subtitle="Control the footer branding shown at the bottom of the chat window."
          >
            <Field label="Show 'Powered by NexaGPT'">
              <Toggle
                value={showBranding}
                onChange={setShowBranding}
                label={showBranding ? "Visible" : "Hidden"}
              />
            </Field>
            {!showBranding && (
              <Field
                label="Custom Footer Text"
                hint="Replace NexaGPT branding with your own (Pro+ only)."
              >
                <TextInput
                  value={brandText}
                  onChange={setBrandText}
                  placeholder="Powered by Acme Corp"
                />
              </Field>
            )}
          </SectionCard>

          {/* 9. Advanced */}
          <SectionCard
            title="Advanced"
            subtitle="Inject custom CSS to override widget styles for fine-grained control."
          >
            <Field label="Custom CSS" hint="Applied inside the chat widget. Use .nexagpt-widget as the root selector.">
              <textarea
                value={customCSS}
                onChange={(e) => setCustomCSS(e.target.value)}
                rows={6}
                placeholder={`.nexagpt-widget {\n  /* your styles here */\n  font-family: 'Helvetica', sans-serif;\n}`}
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30 text-gray-800 resize-none"
              />
            </Field>
          </SectionCard>

        </div>

        {/* ── RIGHT: live preview ── */}
        <div className="w-72 shrink-0 sticky top-0">
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-900">Live Preview</p>
              <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Live
              </span>
            </div>

            <div className="p-3">
              {/* Chat window preview */}
              <div
                className="flex flex-col overflow-hidden border border-gray-200"
                style={{
                  height: 420,
                  borderRadius: borderRadius,
                  boxShadow: showShadow ? "0 8px 24px rgba(0,0,0,0.12)" : "none",
                  fontFamily: fontFamily === "System Default" ? "inherit" : `'${fontFamily}', sans-serif`,
                  background: chatBg,
                }}
              >
                {/* Chat header */}
                <div
                  className="flex items-center gap-2 px-3 py-2.5 shrink-0"
                  style={{ backgroundColor: primaryColor }}
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-sm shrink-0 overflow-hidden bg-white/20"
                  >
                    {botAvatar ? (
                      <img
                        src={URL.createObjectURL(botAvatar)}
                        className="w-full h-full object-cover"
                        alt=""
                      />
                    ) : (
                      <span>🤖</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-white leading-tight truncate">
                      {botName || "Support Bot"}
                    </p>
                    <p className="text-[10px] leading-tight text-white/70">
                      {tagline || "Typically replies instantly"}
                    </p>
                  </div>
                  <div className="w-2 h-2 rounded-full bg-green-400 shrink-0" />
                </div>

                {/* Messages area */}
                <div
                  className="flex-1 overflow-hidden px-3 py-3 space-y-2.5"
                  style={{ background: chatBg }}
                >
                  {/* Bot bubble */}
                  <div className="flex items-end gap-1.5">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] shrink-0"
                      style={{ background: primaryColor }}
                    >
                      🤖
                    </div>
                    <div
                      className="px-2.5 py-1.5 rounded-2xl rounded-bl-sm max-w-[85%] text-[11px] leading-relaxed"
                      style={{ background: botBubbleBg, color: botBubbleText }}
                    >
                      {welcome || "Hi there! How can I help you?"}
                    </div>
                  </div>

                  {/* User bubble */}
                  <div className="flex justify-end">
                    <div
                      className="px-2.5 py-1.5 rounded-2xl rounded-br-sm max-w-[80%] text-[11px] leading-relaxed"
                      style={{ background: userBubbleBg, color: userBubbleText }}
                    >
                      What features do you offer?
                    </div>
                  </div>

                  {/* Bot reply */}
                  <div className="flex items-end gap-1.5">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] shrink-0"
                      style={{ background: primaryColor }}
                    >
                      🤖
                    </div>
                    <div
                      className="px-2.5 py-1.5 rounded-2xl rounded-bl-sm max-w-[85%] text-[11px] leading-relaxed"
                      style={{ background: botBubbleBg, color: botBubbleText }}
                    >
                      I can help with pricing, integrations, setup, and more!
                    </div>
                  </div>

                  {/* Quick reply chips */}
                  {suggestions.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {suggestions.slice(0, 3).map((s, i) => (
                        <span
                          key={i}
                          className="text-[10px] px-2 py-0.5 rounded-full border"
                          style={{
                            borderColor: primaryColor + "55",
                            color: primaryColor,
                          }}
                        >
                          {s.length > 16 ? s.slice(0, 16) + "…" : s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Input bar */}
                <div
                  className="shrink-0 border-t px-2 py-2"
                  style={{ background: "#fff", borderColor: "#e5e7eb" }}
                >
                  <div className="flex items-center gap-1.5">
                    <input
                      disabled
                      placeholder={placeholder || "Ask me anything..."}
                      className="flex-1 px-2.5 py-1.5 border rounded-full bg-gray-50 outline-none"
                      style={{ fontSize: "11px" }}
                    />
                    <button
                      disabled
                      className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: primaryColor }}
                    >
                      <FiSend size={10} color="#fff" />
                    </button>
                  </div>
                </div>

                {/* Footer branding */}
                {showBranding && (
                  <div
                    className="shrink-0 text-center py-1 border-t"
                    style={{ borderColor: "#e5e7eb" }}
                  >
                    <p className="text-[9px] text-gray-400">
                      Powered by{" "}
                      <span className="font-semibold" style={{ color: primaryColor }}>
                        {brandText || "NexaGPT"}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* Launcher button preview */}
              <div className="mt-3 flex items-center justify-between px-1">
                <p className="text-[11px] text-gray-500 font-medium">Launcher</p>
                <div className="flex items-center gap-2">
                  {showBubble && (
                    <div
                      className="text-[10px] text-white px-2 py-1 rounded-lg max-w-[120px] truncate"
                      style={{ background: primaryColor, borderRadius: 8 }}
                    >
                      {bubbleText || "Need help?"}
                    </div>
                  )}
                  <div
                    className="flex items-center justify-center text-white"
                    style={{
                      width: iconSizeMap[iconSize],
                      height: iconSizeMap[iconSize],
                      borderRadius: shapeMap[iconShape],
                      background: primaryColor,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                    }}
                  >
                    <FiMessageCircle size={Math.round(iconSizeMap[iconSize] * 0.4)} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>}
    </div>
  );
}

/* ────────────────── Helper Components ────────────────── */

function SectionCard({ title, subtitle, children }) {
  return (
    <div className="bg-white border rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b">
        <h2 className="font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 text-gray-800"
    />
  );
}

function TextareaInput({ value, onChange }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 text-gray-800 resize-none"
    />
  );
}

function ColorPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-2 h-9">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-9 h-9 rounded-lg cursor-pointer border p-0.5 shrink-0"
      />
      <div className="flex items-center border rounded-lg px-2 py-1.5 flex-1">
        <span className="text-gray-400 text-sm">#</span>
        <input
          value={value.replace("#", "").toUpperCase()}
          maxLength={6}
          onChange={(e) =>
            /^[0-9A-Fa-f]{0,6}$/.test(e.target.value) &&
            onChange("#" + e.target.value)
          }
          className="flex-1 text-sm font-mono text-gray-800 outline-none ml-0.5 w-0"
        />
      </div>
      <div
        className="w-7 h-7 rounded-md border border-gray-200 shrink-0"
        style={{ background: value }}
      />
    </div>
  );
}

function Toggle({ value, onChange, label }) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${
          value ? "bg-primary" : "bg-gray-200"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
            value ? "translate-x-5" : ""
          }`}
        />
      </button>
      <span className="text-sm text-gray-600">{label}</span>
    </div>
  );
}

function AvatarUpload({ label, value, onChange, fallback, locked }) {
  const ref = useRef(null);
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        onClick={() => !locked && ref.current?.click()}
        className={`w-14 h-14 rounded-full border-2 border-dashed border-gray-200 flex items-center justify-center text-2xl overflow-hidden ${
          !locked
            ? "cursor-pointer hover:border-primary/50 transition"
            : "opacity-50 cursor-not-allowed"
        }`}
      >
        {value ? (
          <img
            src={URL.createObjectURL(value)}
            className="w-full h-full object-cover"
            alt=""
          />
        ) : (
          fallback
        )}
      </div>
      <p className="text-xs text-gray-500 text-center leading-tight">{label}</p>
      {locked && (
        <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full font-medium">
          Pro+
        </span>
      )}
      {!locked && (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className="text-[11px] text-primary hover:underline"
        >
          Upload
        </button>
      )}
      <input
        ref={ref}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
    </div>
  );
}
